import { useState, useEffect } from "react";
import { Building, Cog, LogOut, Sun, Moon, Monitor, Flame, UsersRound } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { mainItems, emailItems, otherItems } from "@/components/sidebar/sidebarConfig";
import { useSidebarData } from "@/hooks/useSidebarData";

interface AppSidebarContentProps {
  onNavigate?: () => void;
  hideActiveState?: boolean;
}

export function AppSidebarContent({ onNavigate, hideActiveState = false }: AppSidebarContentProps) {
  const { signOut } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');

  const {
    userOrganization,
    organizationLogo,
    teamCode,
    isAdmin,
    isTeamLeader,
    openTodosCount,
    activeAreaHotLeadsCount
  } = useSidebarData();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' || 'system';
    setTheme(savedTheme);
  }, []);

  const applyTheme = (newTheme: 'light' | 'dark' | 'system') => {
    const root = window.document.documentElement;
    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.remove('light', 'dark');
      root.classList.add(systemTheme);
    } else {
      root.classList.remove('light', 'dark');
      root.classList.add(newTheme);
    }
  };

  const toggleTheme = () => {
    const themes: ('light' | 'dark' | 'system')[] = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    applyTheme(nextTheme);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light': return Sun;
      case 'dark': return Moon;
      default: return Monitor;
    }
  };

  const isActive = (path: string) => hideActiveState ? false : currentPath === path;
  const isDarkMode = document.documentElement.classList.contains('dark');

  const getNavCls = (active: boolean) => {
    if (active) {
      return "flex items-center gap-2 px-2 py-1.5 rounded-md bg-sidebar-primary text-sidebar-primary-foreground font-medium text-sm";
    }
    return isDarkMode
      ? "flex items-center gap-2 px-2 py-1.5 rounded-md text-slate-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-sm"
      : "flex items-center gap-2 px-2 py-1.5 rounded-md text-slate-700 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-sm";
  };

  const handleNavClick = () => {
    onNavigate?.();
  };

  const ThemeIcon = getThemeIcon();

  return (
    <div className="flex flex-col h-full text-sm">
      {/* Header */}
      <div className="p-3 border-b border-sidebar-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {organizationLogo ? (
              <img
                src={organizationLogo}
                alt={userOrganization || "Logo"}
                className="h-7 w-7 object-contain rounded-md"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-7 w-7 bg-gradient-primary rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-xs">
                  {userOrganization ? userOrganization.charAt(0).toUpperCase() : '?'}
                </span>
              </div>
            )}
            <span className="text-base font-semibold">D2D+</span>
          </div>
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="h-7 w-7 p-0" title={`Theme: ${theme}`}>
            <ThemeIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-3">
          {/* Team Section */}
          {userOrganization && (
            <div>
              <p className="text-xs font-medium text-sidebar-foreground/50 mb-1.5 px-2">Team</p>
              <div className="space-y-0.5">
                <div className="flex items-center px-2 py-1.5">
                  <Building className="mr-2 h-4 w-4" />
                  {teamCode && <span className="text-xs text-muted-foreground font-mono">{teamCode}</span>}
                </div>
                {isTeamLeader && (
                  <NavLink to="/team" onClick={handleNavClick} className={getNavCls(isActive('/team'))}>
                    <UsersRound className="h-4 w-4" />
                    <span>Team verwalten</span>
                  </NavLink>
                )}
                {isAdmin && (
                  <NavLink to="/organization" onClick={handleNavClick} className={getNavCls(isActive('/organization'))}>
                    <Cog className="h-4 w-4" />
                    <span>Organisation</span>
                  </NavLink>
                )}
              </div>
            </div>
          )}

          <Separator className="bg-sidebar-border" />

          {/* Main Navigation */}
          <div>
            <p className="text-xs font-medium text-sidebar-foreground/50 mb-1.5 px-2">Hauptmenü</p>
            <div className="space-y-0.5">
              {mainItems.map(item => (
                <NavLink key={item.title} to={item.url} onClick={handleNavClick} className={getNavCls(isActive(item.url))}>
                  <item.icon className="h-4 w-4" />
                  <span className="flex items-center gap-2">
                    {item.title}
                    {item.title === "ToDos" && openTodosCount > 0 && (
                      <span className="bg-gradient-primary/20 text-primary border border-primary/30 px-1.5 py-0.5 rounded-full text-xs font-medium">
                        {openTodosCount}
                      </span>
                    )}
                    {item.title === "Laufliste" && activeAreaHotLeadsCount > 0 && (
                      <span className="bg-red-500/20 text-red-700 border border-red-500/30 px-1.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                        <Flame className="h-3 w-3" />
                        {activeAreaHotLeadsCount}
                      </span>
                    )}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>

          <Separator className="bg-sidebar-border" />

          {/* Email */}
          <div>
            <p className="text-xs font-medium text-sidebar-foreground/50 mb-1.5 px-2">E-Mail</p>
            <div className="space-y-0.5">
              {emailItems.map(item => (
                <NavLink key={item.title} to={item.url} onClick={handleNavClick} className={getNavCls(isActive(item.url))}>
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </NavLink>
              ))}
            </div>
          </div>

          <Separator className="bg-sidebar-border" />

          {/* Other Items */}
          <div>
            <p className="text-xs font-medium text-sidebar-foreground/50 mb-1.5 px-2">Weitere Optionen</p>
            <div className="space-y-0.5">
              {otherItems.map(item => (
                <NavLink key={item.title} to={item.url} onClick={handleNavClick} className={getNavCls(isActive(item.url))}>
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </NavLink>
              ))}
              <button
                onClick={() => { signOut(); handleNavClick(); }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 w-full text-left text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span>Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
